import { Injectable, inject, signal } from "@angular/core";
import { environment } from "@environments/environment";
import { silentSessionExpired } from "@interceptors/silent-request.token";
import { IApiResponse } from "@models/api-response.model";
import {
  IChatBlocked,
  IChatConversation,
  IChatMessage,
  IChatMessagesData,
  IChatProduct,
  IChatSendBody,
  IChatSettings,
  IChatUpload,
  IChatUploadRequest,
} from "@models/chat.model";
import { RequestOptions, RequestService } from "../request/request.service";

/** "?a=1&b=2" from the set values, or "". */
function query(params: Record<string, string | number | undefined>): string {
  const set = Object.entries(params).filter(([, v]) => v !== undefined && v !== "");
  if (!set.length) return "";
  const qs = set.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return `?${qs.join("&")}`;
}

/** How often the sidebar's unread count is refreshed while the tab is visible. */
const UNREAD_POLL_MS = 60_000;

/**
 * Messages with shoppers (merchant-backend /api/v1/chat): the API calls, and
 * the sidebar's unread count (polled only while the document is visible).
 */
@Injectable({
  providedIn: "root",
})
export class ChatService {
  private requestService = inject(RequestService);

  /** Unread messages across all conversations (the sidebar badge). */
  unreadCount = signal<number>(0);

  private unreadTimer: ReturnType<typeof setInterval> | null = null;
  private unreadPolling = false;
  private readonly onVisibility = () => {
    if (!this.unreadPolling) return;
    if (document.visibilityState === "visible") {
      this.refreshUnread();
      this.startUnreadTimer();
    } else {
      this.stopUnreadTimer();
    }
  };

  // ---- Unread badge ----------------------------------------------------------

  startUnreadPolling(): void {
    if (this.unreadPolling) return;
    this.unreadPolling = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    if (document.visibilityState === "visible") {
      this.refreshUnread();
      this.startUnreadTimer();
    }
  }

  stopUnreadPolling(): void {
    this.unreadPolling = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.stopUnreadTimer();
    this.unreadCount.set(0);
  }

  refreshUnread(): void {
    // Session gone (a background poll was refused): stop asking until the next login.
    if (silentSessionExpired()) return;
    this.requestService
      .get<IApiResponse<{ count: number }>>("/api/v1/chat/unread", undefined, {
        background: true,
      })
      .subscribe({
        next: (res) => {
          if (res.status) this.unreadCount.set(Number(res.data?.count) || 0);
        },
        error: () => {},
      });
  }

  private startUnreadTimer(): void {
    this.stopUnreadTimer();
    this.unreadTimer = setInterval(() => this.refreshUnread(), UNREAD_POLL_MS);
  }

  private stopUnreadTimer(): void {
    if (this.unreadTimer) clearInterval(this.unreadTimer);
    this.unreadTimer = null;
  }

  // ---- Conversations -----------------------------------------------------------

  /** `options`: `{ silent }` for the page's own spinner, `{ background }` for refresh polls. */
  listConversations(
    opts: { search?: string; unread?: boolean; offset?: number },
    options: RequestOptions = {}
  ) {
    const qs = query({
      search: opts.search || undefined,
      unread: opts.unread ? 1 : undefined,
      offset: opts.offset || undefined,
    });
    return this.requestService.get<IApiResponse<{ conversations: IChatConversation[] }>>(
      `/api/v1/chat/conversations${qs}`,
      undefined,
      options
    );
  }

  /**
   * The latest page (no cursor), older (`before`) or newer (`after`, polling) messages.
   * `refresh`: ids (at most 20) of shown messages whose current state is wanted back in
   * `refreshed` (videos still processing).
   */
  messages(
    conversationId: number,
    cursor: { before?: number; after?: number; refresh?: number[]; from?: number } = {},
    options: RequestOptions = {}
  ) {
    const qs = query({
      before: cursor.before || undefined,
      after: cursor.after || undefined,
      refresh: cursor.refresh?.length ? cursor.refresh.slice(0, 20).join(",") : undefined,
      from: cursor.after && cursor.from ? cursor.from : undefined,
    });
    return this.requestService.get<IApiResponse<IChatMessagesData>>(
      `/api/v1/chat/conversations/${conversationId}/messages${qs}`,
      undefined,
      options
    );
  }

  send(conversationId: number, body: IChatSendBody) {
    return this.requestService.post<
      IApiResponse<(Partial<IChatBlocked> & { message?: IChatMessage }) | null>
    >(`/api/v1/chat/conversations/${conversationId}/messages`, body, undefined, { silent: true });
  }

  /** `upTo`: the newest message id actually loaded (read up to there, not past it). */
  markRead(conversationId: number, upTo?: number) {
    return this.requestService.post<IApiResponse>(
      `/api/v1/chat/conversations/${conversationId}/read`,
      upTo ? { upTo } : {},
      undefined,
      { silent: true }
    );
  }

  hide(conversationId: number) {
    return this.requestService.post<IApiResponse>(
      `/api/v1/chat/conversations/${conversationId}/hide`,
      {}
    );
  }

  /** Presigned upload URLs, one per file, in order. */
  requestUploads(files: IChatUploadRequest[]) {
    return this.requestService.post<IApiResponse<{ uploads: IChatUpload[] }>>(
      "/api/v1/chat/uploads",
      { files },
      undefined,
      { silent: true }
    );
  }

  // ---- Settings and products ---------------------------------------------------

  getSettings() {
    return this.requestService.get<IApiResponse<IChatSettings>>("/api/v1/chat/settings");
  }

  saveSettings(settings: IChatSettings) {
    return this.requestService.put<IApiResponse<IChatSettings>>("/api/v1/chat/settings", settings);
  }

  products(search: string) {
    const qs = query({ search: search || undefined });
    return this.requestService.get<IApiResponse<{ products: IChatProduct[] }>>(
      `/api/v1/chat/products${qs}`,
      undefined,
      { silent: true }
    );
  }
}

/**
 * A message photo/video storage key as a URL on the public CDN. Only relative `chat/...`
 * keys are accepted; anything else (an absolute URL, a non-string) gives "" - nothing shown.
 */
export function chatMediaUrl(path: unknown): string {
  if (typeof path !== "string" || !/^chat\/[\w\-./]+$/.test(path) || path.includes("..")) {
    return "";
  }
  return `${environment.chatMediaCdnURL}/${path}`;
}

/** The storefront page of a product: /<iso2>/product-detail/<slug>-<id>. */
export function storefrontProductUrl(iso2: string, productName: string | null, productId: number) {
  const slug = (productName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `https://fishyhub.com/${(iso2 || "sg").toLowerCase()}/product-detail/${slug}-${productId}`;
}
